import styled, { keyframes, css } from "styled-components";

const theme = {
  bg: "#F8F9FA",
  surface: "#FFFFFF",
  glass: "rgba(255, 255, 255, 0.6)",
  border: "#E9ECEF",
  primary: "#007BFF",
  secondary: "#6F42C1",
  accent: "#20C997",
  text: "#212529",
  textSecondary: "#6C757D",
  danger: "#DC3545",
  success: "#28A745",
  warning: "#FFC107",
};

const aurora = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const PaginaContainer = styled.div`
  min-height: 100vh;
  padding: 3rem 2rem;
  background-color: ${theme.bg};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(0, 123, 255, 0.05) 0%,
      rgba(111, 66, 193, 0.04) 35%,
      rgba(248, 249, 250, 0) 70%
    );
    animation: ${aurora} 25s ease-in-out infinite;
    z-index: 0;
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeIn} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const BackButton = styled.button`
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  color: ${theme.textSecondary};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.8rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;

  &:hover {
    background: ${theme.primary};
    color: white;
    border-color: ${theme.primary};
    transform: scale(1.1) rotate(-15deg);
    box-shadow: 0 6px 15px rgba(0, 123, 255, 0.2);
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.text};

  span {
    font-weight: 400;
    color: ${theme.textSecondary};
  }
`;

export const DetailsCard = styled.div`
  background: ${theme.glass};
  border: 1px solid ${theme.border};
  border-radius: 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.07);
  overflow: hidden;
`;

export const CardBody = styled.div`
  padding: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2.5rem;
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const InfoLabel = styled.span`
  font-size: 0.9rem;
  color: ${theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${theme.secondary};
  }
`;

export const InfoValue = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  color: ${theme.text};
  word-wrap: break-word;

  &.highlight {
    font-size: 2.5rem;
    font-weight: 700;
    background: linear-gradient(45deg, ${theme.primary}, ${theme.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
`;

const getStatusStyles = (status) => {
  switch (status) {
    case 1:
      return css`
        background-color: rgba(255, 193, 7, 0.1);
        color: #b08000;
      `;
    case 2:
      return css`
        background-color: rgba(40, 167, 69, 0.1);
        color: ${theme.success};
      `;
    case 3:
      return css`
        background-color: rgba(220, 53, 69, 0.1);
        color: ${theme.danger};
      `;
    default:
      return css`
        background-color: rgba(108, 117, 125, 0.1);
        color: ${theme.textSecondary};
      `;
  }
};

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  ${({ status }) => getStatusStyles(status)}
`;

export const CardFooter = styled.div`
  padding: 1.5rem 3rem;
  border-top: 1px solid ${theme.border};
  display: flex;
  justify-content: flex-end;
  background-color: rgba(0, 0, 0, 0.02);
  gap: 1rem;
`;

export const ActionButton = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background-color: ${theme.danger};
  color: white;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);

  &:hover {
    transform: translateY(-3px) scale(1.05);
    background-color: #c82333;
    box-shadow: 0 7px 20px rgba(220, 53, 69, 0.3);
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
  }
`;

export const SecondaryButton = styled.a`
  padding: 0.8rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid ${theme.border};
  background-color: ${theme.surface};
  color: ${theme.text};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-3px);
    background-color: ${theme.bg};
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  }
`;

// Botão bonito para ver o recibo no Header
export const ReceiptButton = styled.a`
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${theme.surface};
  color: ${theme.primary};
  border: 1px solid ${theme.primary};
  box-shadow: 0 2px 5px rgba(0, 123, 255, 0.1);

  &:hover {
    background: ${theme.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
  color: ${theme.textSecondary};
  font-size: 1.5rem;
  font-weight: 500;
  position: relative;
  z-index: 1;
`;
